import React, { useMemo } from "react"
import { Input, Select, Switch, Checkbox, Spin } from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { translations } from "../../../utils/Translation/staticTranslation"
import {
  getAllowedFeaturesCatalog,
  getFeatureLabelFromCatalog,
  isIncludedUseFeatureKey,
  isPerGenerationFeatureKey,
} from "../../../utils/plansPricingPayload"
import { PLAN_METADATA_SECTIONS } from "../planMetadataSections"
import "./CreatePlan.css"

const { TextArea } = Input
const { Option } = Select

const CreatePlanForm = ({
  form,
  onChange,
  isReadOnly,
  isEditMode = false,
  isSuperAdmin = false,
  planFeaturesConfig = null,
  loadingFeatures = false,
}) => {
  const lang = localStorage.getItem("language") || "ar"
  const tPage = translations[lang]?.plansPricingPage
  const t = tPage?.createPlan

  const catalogFeatures = useMemo(
    () => getAllowedFeaturesCatalog(planFeaturesConfig),
    [planFeaturesConfig]
  )

  const perGenerationFeatures = useMemo(
    () =>
      catalogFeatures.filter((f) =>
        isPerGenerationFeatureKey(f.key, planFeaturesConfig)
      ),
    [catalogFeatures, planFeaturesConfig]
  )

  const includedUseFeatures = useMemo(
    () =>
      catalogFeatures.filter((f) =>
        isIncludedUseFeatureKey(f.key, planFeaturesConfig)
      ),
    [catalogFeatures, planFeaturesConfig]
  )

  const set = (field, value) => {
    if (!isReadOnly) onChange({ ...form, [field]: value })
  }

  const MAX_COUNT_FIELD_DIGITS = 10

  const toDigitsOnly = (value, maxLength = MAX_COUNT_FIELD_DIGITS) =>
    String(value ?? "")
      .replace(/\D/g, "")
      .slice(0, maxLength)

  const handleDigitsFocus = (field) => {
    if (!isReadOnly && String(form[field] ?? "") === "0") {
      set(field, "")
    }
  }

  const handleDigitsBlur = (field) => {
    const digits = toDigitsOnly(form[field])
    set(field, digits === "" ? "0" : String(parseInt(digits, 10)))
  }

  const maxDesc = 100

  const visibleFeatures = form.features.filter(
    (f) => f.category === form.activeFeatureTab
  )

  const handleAddFeature = () => {
    if (isReadOnly) return
    onChange({
      ...form,
      features: [
        ...form.features,
        {
          id: String(Date.now()),
          category: form.activeFeatureTab,
          label: "",
          labelAr: "",
        },
      ],
    })
  }

  const handleDeleteFeature = (id) => {
    if (isReadOnly) return
    onChange({
      ...form,
      features: form.features.filter((f) => f.id !== id),
    })
  }

  const handleFeatureLabelChange = (id, field, value) => {
    if (isReadOnly) return
    onChange({
      ...form,
      features: form.features.map((f) =>
        f.id === id ? { ...f, [field]: value } : f
      ),
    })
  }

  const toggleAllowedFeature = (key, checked) => {
    if (isReadOnly) return
    const allowed = new Set(form.allowedFeatures || [])
    if (checked) allowed.add(key)
    else allowed.delete(key)
    onChange({
      ...form,
      allowedFeatures: [...allowed],
    })
  }

  const renderAllowedFeatureRow = (feature) => {
    const featureKey = feature.key
    const isEnabled = (form.allowedFeatures || []).includes(featureKey)
    const showImageCost = isPerGenerationFeatureKey(
      featureKey,
      planFeaturesConfig
    )

    return (
      <div
        key={featureKey}
        className={`create-plan-allowed-feature-row ${
          isEnabled ? "is-enabled" : ""
        }`}
      >
        <Checkbox
          checked={isEnabled}
          onChange={(e) => toggleAllowedFeature(featureKey, e.target.checked)}
          disabled={isReadOnly}
        >
          <span className="create-plan-allowed-feature-label">
            {getFeatureLabelFromCatalog(feature, lang)}
          </span>
        </Checkbox>
        {showImageCost && (
          <div className="create-plan-allowed-feature-cost">
            <label className="create-plan-label create-plan-label--sm">
              {t?.images_per_generation || "Images per generation"}
            </label>
            <Input
              placeholder="0"
              value={form.featureImageCosts?.[featureKey] ?? "0"}
              onChange={(e) => setFeatureImageCost(featureKey, e.target.value)}
              disabled={isReadOnly || !isEnabled}
            />
          </div>
        )}
      </div>
    )
  }

  const setFeatureImageCost = (key, value) => {
    if (isReadOnly) return
    onChange({
      ...form,
      featureImageCosts: {
        ...(form.featureImageCosts || {}),
        [key]: value,
      },
    })
  }

  return (
    <div className="create-plan-form-column">
      <section className="create-plan-card">
        <h2 className="create-plan-card-title">
          {t?.basic_info || "Basic Information"}
        </h2>
        <div className="create-plan-card-body">
          <div className="create-plan-field create-plan-field--en">
            <label className="create-plan-label">
              {t?.plan_name || "Plan Name"}{" "}
              <span className="required">*</span>
            </label>
            <Input
              dir="ltr"
              placeholder={
                t?.plan_name_placeholder || "e.g., Professional Plan"
              }
              value={form.planName}
              onChange={(e) => set("planName", e.target.value)}
              disabled={isReadOnly}
            />
          </div>

          <div className="create-plan-field create-plan-field--ar">
            <label className="create-plan-label">
              {t?.plan_name_arabic || "Plan Name (Arabic)"}{" "}
              <span className="required">*</span>
            </label>
            <Input
              dir="rtl"
              placeholder={
                translations.ar?.plansPricingPage?.createPlan
                  ?.plan_name_arabic_placeholder ||
                t?.plan_name_arabic_placeholder ||
                "مثال: باقة احترافية"
              }
              value={form.planNameArabic}
              onChange={(e) => set("planNameArabic", e.target.value)}
              disabled={isReadOnly}
            />
          </div>

          <div className="create-plan-field create-plan-field--en">
            <label className="create-plan-label">
              {t?.short_description || "Short Description"}{" "}
              <span className="required">*</span>
            </label>
            <TextArea
              dir="ltr"
              rows={3}
              maxLength={maxDesc}
              placeholder={
                t?.short_description_placeholder ||
                "Brief description of this plan"
              }
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              disabled={isReadOnly}
              className="create-plan-textarea"
            />
            <div className="create-plan-char-count">
              {t?.max_chars || "Maximum"} {maxDesc}{" "}
              {t?.characters || "characters"}
            </div>
          </div>

          <div className="create-plan-field create-plan-field--ar">
            <label className="create-plan-label">
              {t?.short_description_arabic || "Short Description (Arabic)"}{" "}
              <span className="required">*</span>
            </label>
            <TextArea
              dir="rtl"
              rows={3}
              maxLength={maxDesc}
              placeholder={
                translations.ar?.plansPricingPage?.createPlan
                  ?.short_description_arabic_placeholder ||
                t?.short_description_arabic_placeholder ||
                "وصف موجز لهذه الخطة"
              }
              value={form.descriptionArabic}
              onChange={(e) => set("descriptionArabic", e.target.value)}
              disabled={isReadOnly}
              className="create-plan-textarea"
            />
          </div>

          <div
            className={`create-plan-row${
              isSuperAdmin ? " create-plan-row--triple" : ""
            }`}
          >
            {isSuperAdmin && (
              <div className="create-plan-field">
                <label className="create-plan-label">
                  {t?.category || tPage?.table?.category || "Category"}
                </label>
                <Select
                  value={form.planCategory || "standard"}
                  onChange={(v) => set("planCategory", v)}
                  disabled={isReadOnly}
                  className="create-plan-select-full"
                >
                  <Option value="standard">
                    {tPage?.filters?.categories?.standard || "Standard"}
                  </Option>
                  <Option value="organization">
                    {tPage?.filters?.categories?.organization || "Organization"}
                  </Option>
                </Select>
              </div>
            )}
            <div className="create-plan-field">
              <label className="create-plan-label">
                {t?.plan_type || "Plan type"}
              </label>
              <Select
                value={form.planType}
                onChange={(v) => set("planType", v)}
                disabled={isReadOnly}
                className="create-plan-select-full"
              >
                <Option value="Subscription">
                  {t?.type_subscription || "Subscription"}
                </Option>
                <Option value="One-Time">
                  {t?.type_one_time || "One-Time"}
                </Option>
              </Select>
            </div>
            <div className="create-plan-field">
              <label className="create-plan-label">
                {t?.billing_cycle || "Billing Cycle"}
              </label>
              {form.planType === "Subscription" ? (
                <Select
                  value={form.billingCycle}
                  onChange={(v) => set("billingCycle", v)}
                  disabled={isReadOnly || isEditMode}
                  className="create-plan-select-full"
                >
                  <Option value="Monthly">
                    {t?.billing_monthly_option || "Monthly"}
                  </Option>
                  <Option value="Yearly">
                    {t?.billing_yearly_option || "Yearly"}
                  </Option>
                </Select>
              ) : (
                <Select
                  value="none"
                  disabled
                  className="create-plan-select-full"
                >
                  <Option value="none">—</Option>
                </Select>
              )}
            </div>
            {isEditMode ? (
              <div className="create-plan-field">
                <label className="create-plan-label">
                  {t?.sub_name || "Sub name"}
                </label>
                <div className="create-plan-readonly-value">
                  {form.subName || "—"}
                </div>
              </div>
            ) : null}
          </div>

          <div className="create-plan-row">
            <div className="create-plan-field">
              <label className="create-plan-label">
                {t?.price || "Price"} <span className="required">*</span>
              </label>
              <Input
                className="create-plan-price-input"
                addonBefore="SAR"
                placeholder="0.00"
                value={form.price}
                onChange={(e) => set("price", e.target.value)}
                disabled={isReadOnly}
              />
            </div>
            <div className="create-plan-field">
              <label className="create-plan-label">
                {(t?.price_usd || "Price (USD)")} <span className="required">*</span>
              </label>
              <Input
                className="create-plan-price-input"
                addonBefore="USD"
                placeholder="0.00"
                value={form.priceUsd}
                onChange={(e) => set("priceUsd", e.target.value)}
                disabled={isReadOnly}
              />
            </div>
            <div className="create-plan-field">
              <label className="create-plan-label">
                {t?.images || "Images"}
              </label>
              <Input
                placeholder="0"
                inputMode="numeric"
                maxLength={MAX_COUNT_FIELD_DIGITS}
                value={form.images}
                onFocus={() => handleDigitsFocus("images")}
                onBlur={() => handleDigitsBlur("images")}
                onChange={(e) => set("images", toDigitsOnly(e.target.value))}
                disabled={isReadOnly}
              />
            </div>
            <div className="create-plan-field">
              <label className="create-plan-label">
                {t?.furniture_finder || "Furniture Finder"}
              </label>
              <Input
                placeholder="0"
                inputMode="numeric"
                maxLength={MAX_COUNT_FIELD_DIGITS}
                value={form.furnitureFinder}
                onFocus={() => handleDigitsFocus("furnitureFinder")}
                onBlur={() => handleDigitsBlur("furnitureFinder")}
                onChange={(e) =>
                  set("furnitureFinder", toDigitsOnly(e.target.value))
                }
                disabled={isReadOnly}
              />
            </div>
          </div>

          {isSuperAdmin ? (
            <>
              <div className="create-plan-settings-row">
                <div className="create-plan-settings-text">
                  <h4>{t?.apply_discount || "Apply discount"}</h4>
                  <p>
                    {t?.apply_discount_desc ||
                      "Enable to set discounted prices in SAR and USD."}
                  </p>
                </div>
                <Switch
                  checked={Boolean(form.discounted)}
                  onChange={(checked) => {
                    if (isReadOnly) return
                    onChange({
                      ...form,
                      discounted: checked,
                      ...(checked
                        ? {}
                        : { discountedPrice: "", discountedPriceUsd: "" }),
                    })
                  }}
                  disabled={isReadOnly}
                />
              </div>

              {form.discounted ? (
                <div className="create-plan-row">
                  <div className="create-plan-field">
                    <label className="create-plan-label">
                      {t?.discounted_price || "Discounted Price"}{" "}
                      <span className="required">*</span>
                    </label>
                    <Input
                      className="create-plan-price-input"
                      addonBefore="SAR"
                      placeholder="0.00"
                      value={form.discountedPrice}
                      onChange={(e) => set("discountedPrice", e.target.value)}
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="create-plan-field">
                    <label className="create-plan-label">
                      {t?.discounted_price_usd || "Discounted Price (USD)"}{" "}
                      <span className="required">*</span>
                    </label>
                    <Input
                      className="create-plan-price-input"
                      addonBefore="USD"
                      placeholder="0.00"
                      value={form.discountedPriceUsd}
                      onChange={(e) =>
                        set("discountedPriceUsd", e.target.value)
                      }
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </section>

      <section className="create-plan-card">
        <h2 className="create-plan-card-title">{t?.features || "Features"}</h2>
        <div className="create-plan-card-body">
          <div className="create-plan-feature-tabs">
            {PLAN_METADATA_SECTIONS.map((tab) => {
              const Icon = tab.Icon
              const isActive = form.activeFeatureTab === tab.key
              return (
                <button
                  key={tab.key}
                  type="button"
                  className={`create-plan-feature-tab ${
                    isActive ? "active" : ""
                  }`}
                  onClick={() => set("activeFeatureTab", tab.key)}
                >
                  <Icon className="create-plan-feature-tab-icon" />
                  {t?.[tab.labelKey] || tab.key}
                </button>
              )
            })}
          </div>

          <div className="create-plan-feature-list">
            {visibleFeatures.length === 0 ? (
              <p className="create-plan-no-features">
                {t?.no_features || "No features in this category yet."}
              </p>
            ) : (
              visibleFeatures.map((feature) => (
                <div key={feature.id} className="create-plan-feature-item">
                  <div className="create-plan-feature-inputs">
                    <div className="create-plan-feature-input-wrap create-plan-feature-input-wrap--en">
                      <label className="create-plan-label">
                        {t?.feature_description || "Feature description"}{" "}
                        <span className="required">*</span>
                      </label>
                      <Input
                        dir="ltr"
                        value={feature.label ?? ""}
                        onChange={(e) =>
                          handleFeatureLabelChange(
                            feature.id,
                            "label",
                            e.target.value
                          )
                        }
                        placeholder={
                          t?.feature_placeholder ||
                          "Enter feature description (English)"
                        }
                        disabled={isReadOnly}
                      />
                    </div>
                    <div className="create-plan-feature-input-wrap create-plan-feature-input-wrap--ar">
                      <label className="create-plan-label">
                        {t?.feature_description_arabic ||
                          "Feature description (Arabic)"}{" "}
                        <span className="required">*</span>
                      </label>
                      <Input
                        value={feature.labelAr ?? ""}
                        onChange={(e) =>
                          handleFeatureLabelChange(
                            feature.id,
                            "labelAr",
                            e.target.value
                          )
                        }
                        placeholder={
                          t?.feature_placeholder_arabic ||
                          "Enter feature description (Arabic)"
                        }
                        disabled={isReadOnly}
                        dir="rtl"
                      />
                    </div>
                  </div>
                  {!isReadOnly && (
                    <DeleteOutlined
                      className="create-plan-feature-delete"
                      onClick={() => handleDeleteFeature(feature.id)}
                    />
                  )}
                </div>
              ))
            )}
          </div>

          <button
            type="button"
            className="create-plan-add-feature-btn"
            onClick={handleAddFeature}
            disabled={isReadOnly}
          >
            <PlusOutlined /> {t?.add_feature || "Add Feature"}
          </button>

          <div className="create-plan-features-subsection">
            <h3 className="create-plan-subsection-title">
              {t?.allowed_features || "Allowed Features"}
            </h3>
            <p className="create-plan-section-desc">
              {t?.allowed_features_desc ||
                "Select features included in this plan. Per-generation features deduct images from the plan image pool on each use."}
            </p>
            {loadingFeatures ? (
              <div className="create-plan-allowed-features-loading">
                <Spin size="small" />
              </div>
            ) : catalogFeatures.length === 0 ? (
              <p className="create-plan-no-features">
                {t?.no_allowed_features || "No features available."}
              </p>
            ) : (
              <>
                {perGenerationFeatures.length > 0 && (
                  <div className="create-plan-allowed-features">
                    {includedUseFeatures.length > 0 ? (
                      <p className="create-plan-allowed-group-label">
                        {t?.per_generation_features || "Per generation"}
                      </p>
                    ) : null}
                    {perGenerationFeatures.map(renderAllowedFeatureRow)}
                  </div>
                )}
                {includedUseFeatures.length > 0 && (
                  <div className="create-plan-allowed-features create-plan-allowed-features--spaced">
                    <p className="create-plan-allowed-group-label">
                      {t?.included_use_features || "Included use"}
                    </p>
                    {includedUseFeatures.map(renderAllowedFeatureRow)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="create-plan-card create-plan-card--compact">
        <h2 className="create-plan-card-title">
          {t?.plan_settings || "Plans Settings"}
        </h2>
        <div className="create-plan-settings-row">
          <div className="create-plan-settings-text">
            <h4>{t?.active_status || "Active Status"}</h4>
            <p>
              {t?.active_status_desc ||
                "Make this plan available to users"}
            </p>
          </div>
          <Switch
            checked={form.activeStatus}
            onChange={(v) => set("activeStatus", v)}
            disabled={isReadOnly}
          />
        </div>
      </section>
    </div>
  )
}

export default CreatePlanForm
